import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, PropsWithChildren, useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { Loader } from 'src/components/Loader';
import { trpc } from 'src/trpc';
import { type ContainerZ, containerStatuses, containerZ } from '@libs/schemas';
import { NumericFormat } from 'react-number-format';
import { ErrorMessage } from '@hookform/error-message';
import { useToast } from 'src/components/useToast';
import { MoneyFromCents } from '@libs/services/money';

type FormState = {
  isValid: boolean;
};

interface Props extends PropsWithChildren {
  container?: ContainerZ;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onStateChange?: (state: FormState) => void;
}

export const ContainerDetailsForm: FC<Props> = ({
  container,
  onSuccess,
  onError,
  onStateChange,
  children,
}) => {
  const utils = trpc.useUtils();
  const { errorToast } = useToast();

  const { data: containerTypesData, isLoading: containerTypesLoading } =
    trpc.admin.containerType.getAllContainerTypes.useQuery({ archived: 'active' });
  const { mutate, isPending } = trpc.admin.container.upsertContainer.useMutation({
    onSuccess: () => {
      onSuccess?.();
      utils.admin.container.getAllContainers.invalidate();
    },

    onError: (error) => {
      onError?.(new Error(error.message || 'An unknown error occurred'));
    },
  });

  const form = useForm({
    resolver: zodResolver(containerZ),
    defaultValues: {
      status: 'available',
      containerTypeInfo: {
        name: '',
        size: undefined,
        maxWeight: 0,
        volume: '',
        pricePerDay: 0,
        _id: '',
      },
    } as unknown as ContainerZ,
    values: container
      ? {
          ...container,
          containerTypeInfo: {
            ...container.containerTypeInfo,
            pricePerDay: MoneyFromCents(container.containerTypeInfo.pricePerDay).toUnit(),
          },
        }
      : undefined,
  });

  const { isValid, errors } = useFormState({ control: form.control });

  useEffect(() => {
    onStateChange?.({ isValid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const onSubmit = (values: ContainerZ) => {
    // Make sure containerTypeInfo is fully populated before submitting
    if (
      !values.containerTypeInfo?._id &&
      containerTypesData?.containerTypes &&
      containerTypesData.containerTypes.length > 0
    ) {
      errorToast({
        title: 'Missing container size',
        description: 'Please select a container size before submitting',
      });
      return;
    }
    mutate(values);
  };

  // Build display list — include the current container's type even if it has since been archived
  const containerTypesList = containerTypesData?.containerTypes ?? [];
  const displayContainerTypes = (() => {
    if (container?.containerTypeInfo?._id) {
      const currentId = container.containerTypeInfo._id?.toString();
      const found = containerTypesList.some((t) => t._id?.toString() === currentId);
      if (!found) {
        return [
          ...containerTypesList,
          {
            ...container.containerTypeInfo,
            name: `${container.containerTypeInfo.name} (archived)`,
          },
        ];
      }
    }
    return containerTypesList;
  })();

  if (containerTypesLoading) {
    return <Loader title="Getting dumpsters" />;
  }

  if (isPending) {
    return <Loader title="Updating dumpster details" />;
  }

  // Check if container types data is available
  if (!containerTypesData || displayContainerTypes.length === 0) {
    return (
      <div className="text-destructive">
        No container types available. Please add container types first.
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-row gap-4">
            <FormField
              control={form.control}
              name="containerTypeInfo._id"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="label">Size</FormLabel>
                  <FormControl>
                    <Select
                      {...field}
                      onValueChange={(value) => {
                        const containerType = displayContainerTypes.find(
                          (type) => type._id?.toString() === value,
                        );
                        if (containerType) {
                          const { name, maxWeight, pricePerDay, volume, size, _id } = containerType;

                          form.setValue('containerTypeInfo', {
                            size,
                            pricePerDay: MoneyFromCents(pricePerDay).toUnit(),
                            maxWeight,
                            volume,
                            _id: _id?.toString() ?? '',
                            name,
                          });
                          form.trigger();
                        }
                      }}
                    >
                      <SelectTrigger className="text-text-light">
                        <SelectValue
                          className="text-text-light"
                          placeholder="Select a dumpster size"
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {displayContainerTypes.map((type) => (
                          <SelectItem key={type._id?.toString()} value={type._id?.toString() ?? ''}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <ErrorMessage errors={errors.containerTypeInfo?._id} name="containerType" />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="containerTypeInfo.pricePerDay"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="label mb-1 justify-start">Price Per Day</FormLabel>
                  <FormControl>
                    <NumericFormat
                      {...field}
                      displayType={'input'}
                      thousandSeparator={true}
                      prefix={'$'}
                      disabled
                      customInput={Input}
                      placeholder="Enter price per day"
                    />
                  </FormControl>
                  <ErrorMessage errors={errors.containerTypeInfo?.pricePerDay} name="pricePerDay" />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex flex-row gap-4">
            <FormField
              control={form.control}
              name="containerTypeInfo.maxWeight"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="label">Max Weight</FormLabel>
                  <FormControl>
                    <NumericFormat
                      {...field}
                      displayType={'input'}
                      thousandSeparator={true}
                      customInput={Input}
                      disabled
                      placeholder="Enter max weight"
                      suffix=" Lbs"
                    />
                  </FormControl>
                  <ErrorMessage errors={errors.containerTypeInfo?.maxWeight} name="maxWeight" />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="containerTypeInfo.volume"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="label">Volume</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Enter volume" disabled />
                  </FormControl>
                  <ErrorMessage errors={errors.containerTypeInfo?.volume} name="volume" />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label">Status</FormLabel>
                <FormControl>
                  <Select {...field} onValueChange={(value) => field.onChange(value)}>
                    <SelectTrigger className="text-text-light">
                      <div className="flex flex-row gap-2">
                        <SelectValue placeholder={'Select a status'} />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {containerStatuses.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <ErrorMessage errors={errors.status} name="status" />
                <FormMessage />
              </FormItem>
            )}
          />
          {children}
        </div>
      </form>
    </Form>
  );
};
